import { useMemo } from "react";
import { Text, Tooltip } from "@fluentui/react-components";
import { Info16Regular } from "@fluentui/react-icons";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Constants } from "common/constants";
import { IAdoRunRecord } from "../../api/aiMetricsApi";
import { useAdoRefreshStatus } from "../../hooks/useAdoRefresh";
import { RefreshHistoryPopover } from "./RefreshHistoryPopover";
import { useRefreshControlStyles } from "./RefreshControl.styles";
import { formatLocalDateTime } from "utils/generalUtils";

const buildLastRefreshTooltip = (lastRefresh: IAdoRunRecord | null): string => {
  if (!lastRefresh) return "No refresh has been completed yet.";

  const triggeredByName = lastRefresh.triggered_by?.display_name;
  const triggerLabel =
    lastRefresh.trigger === "on_demand"
      ? `Manual refresh${triggeredByName ? ` by ${triggeredByName}` : ""}`
      : "Scheduled refresh";
  const { start_date: startDate, end_date: endDate } = lastRefresh.task_args;

  if (!startDate && !endDate) {
    return `${triggerLabel} covering all available data.`;
  }
  const start = startDate ?? "earliest";
  const end = endDate ?? "latest";
  return `${triggerLabel} covering ${start} to ${end}.`;
};

export const RefreshControl = (): JSX.Element => {
  const styles = useRefreshControlStyles();

  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const isAdmin = !!currentUser?.roles?.includes(
    Constants.AI_METRICS_ADMINS_GROUP_NAME_VALUE,
  );

  const { data: refreshStatus } = useAdoRefreshStatus();

  const lastRefreshDisplay = useMemo(
    () => formatLocalDateTime(refreshStatus?.last_refresh?.started_at ?? null),
    [refreshStatus?.last_refresh?.started_at],
  );

  const nextScheduledDisplay = useMemo(
    () => formatLocalDateTime(refreshStatus?.next_scheduled ?? null),
    [refreshStatus?.next_scheduled],
  );

  const lastRefreshTooltip = useMemo(
    () => buildLastRefreshTooltip(refreshStatus?.last_refresh ?? null),
    [refreshStatus?.last_refresh],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.timestampGroup}>
        <span className={styles.sectionLabel}>
          Last Refresh
          <Tooltip
            content={lastRefreshTooltip}
            relationship="description"
            positioning="above"
          >
            <button
              type="button"
              className={styles.infoIconButton}
              aria-label="Last refresh details"
            >
              <Info16Regular />
            </button>
          </Tooltip>
        </span>
        <div className={styles.timestampValueRow}>
          <Text className={styles.timestampValue}>{lastRefreshDisplay}</Text>
          {isAdmin && <RefreshHistoryPopover />}
        </div>
      </div>

      <div className={styles.timestampGroup}>
        <span className={styles.sectionLabel}>Next Scheduled</span>
        <Text className={styles.timestampValue}>{nextScheduledDisplay}</Text>
      </div>
    </div>
  );
};
